import * as React from 'react';
import { displayNameForIntegrationType, iconForJiraTicketType } from '@/helpers/integrationHelpers';
import Tag from 'antd/lib/tag';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Collapse from 'antd/lib/collapse';
import Dropdown from 'antd/lib/dropdown';
import Modal from 'antd/lib/modal';
import Image from 'antd/lib/image';
import Rate from 'antd/lib/rate';
import Text from 'antd/lib/typography/Text';
import Paragraph from 'antd/lib/typography/Paragraph';
import Tooltip from 'antd/lib/tooltip';
import dayjs from 'dayjs';
import { DISALLOWED_ACTION_ERROR_MESSAGE } from '@/components/HOCs/PermissionedActionButton';
import {
    CloseCircleOutlined,
    EllipsisOutlined,
    ExportOutlined,
    MinusCircleOutlined,
    RightOutlined,
} from '@ant-design/icons';
import { IntegrationType, IssueSource, IssueSourceType } from '@/types';
import { antGrey, drawerSectionSeparator, getAvatarColorForName, rwColors } from '@/components/styles/globalStyles';
import { tagColorForStatusCategory } from '@/helpers';
import { formatDateWithTimezone } from '@/timeHelpers';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';
import sparkleIcon from '../../../assets/sparkle.svg';
import MarkdownParagraphWithEllipsis from '@/components/HOCs/MarkdownParagraphWithEllipsis';
import { DESCRIPTION_MARKDOWN_COMPONENTS, IssueSourceIcon } from './triageHelpers';

const ROW_STYLE: React.CSSProperties = {
    margin: '0 16px',
    padding: '12px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
};

const ROW_BORDER_STYLE: React.CSSProperties = { ...ROW_STYLE, borderTop: `1px solid ${antGrey.grey5}` };

const personRow = (label: string, name?: string) => {
    if (!name) {
        return null;
    }
    return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{label}</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar
                    size={20}
                    style={{ backgroundColor: getAvatarColorForName(name), fontSize: 11, border: 'none' }}
                >
                    {name.charAt(0).toUpperCase()}
                </Avatar>
                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{name}</Text>
            </div>
        </div>
    );
};

const PMTicketSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const ticket = source.pmTicket;
    const ticketTypeIcon =
        ticket?.integrationType === IntegrationType.Jira ? iconForJiraTicketType(ticket.ticketType) : undefined;
    if (!ticket) {
        return null;
    }
    return (
        <>
            <div style={ROW_STYLE}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                    <div>
                        {ticket.projectName &&
                            (ticket.projectUrl ?
                                <a href={ticket.projectUrl} target="_blank" rel="noreferrer">
                                    <Text style={{ color: rwColors.primary, fontSize: 14 }}>{ticket.projectName}</Text>
                                </a>
                            :   <Text style={{ color: antGrey.grey7, fontSize: 14 }}>{ticket.projectName}</Text>)}
                    </div>
                    {ticket.ticketUrl && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                            {ticket.parentTicketId && (
                                <Tooltip title={`Subtask of ${ticket.parentTicketId}`}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                        <Text style={{ color: antGrey.grey7, fontSize: 14 }}>
                                            {ticket.parentTicketId}
                                        </Text>
                                        <RightOutlined style={{ color: antGrey.grey6, fontSize: 10 }} />
                                    </span>
                                </Tooltip>
                            )}
                            <a
                                href={ticket.ticketUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            >
                                {ticketTypeIcon && (
                                    <img
                                        src={ticketTypeIcon}
                                        alt={ticket.ticketType}
                                        title={ticket.ticketType}
                                        style={{ width: 16, height: 16 }}
                                    />
                                )}
                                <Text style={{ color: rwColors.primary, fontSize: 14 }}>{ticket.ticketId}</Text>
                            </a>
                        </div>
                    )}
                </div>
                <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{ticket.ticketTitle}</Text>
                {ticket.labels?.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {ticket.labels.map((label) => (
                            <Tag
                                key={label}
                                style={{
                                    margin: 0,
                                    background: antGrey.grey4,
                                    border: `1px solid ${antGrey.grey4}`,
                                    color: antGrey.grey8,
                                }}
                            >
                                {label}
                            </Tag>
                        ))}
                    </div>
                )}
            </div>
            {(ticket.ticketDescription || ticket.assignee) && (
                <div style={ROW_BORDER_STYLE}>
                    {ticket.ticketDescription && (
                        <MarkdownParagraphWithEllipsis
                            content={ticket.ticketDescription}
                            maxHeight="160px"
                            backgroundColor="transparent"
                            style={{ color: antGrey.grey8, fontSize: 14 }}
                            markdownProps={{ components: DESCRIPTION_MARKDOWN_COMPONENTS }}
                            toggleAlign="right"
                        />
                    )}
                    {personRow('Assignee', ticket.assignee)}
                </div>
            )}
            {ticket.reporter && <div style={ROW_BORDER_STYLE}>{personRow('Reporter', ticket.reporter)}</div>}
            {ticket.fixVersion && (
                <div style={ROW_BORDER_STYLE}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>Fix version</Text>
                        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{ticket.fixVersion}</Text>
                    </div>
                </div>
            )}
        </>
    );
};

const AI_SUMMARY_STYLE: React.CSSProperties = {
    ...ROW_STYLE,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    padding: '12px 0 20px',
    borderBottom: `1px dashed ${antGrey.grey5}`,
};

const AISummaryRow: React.FC<{ description?: string }> = ({ description }) => {
    if (!description) {
        return null;
    }
    return (
        <div style={AI_SUMMARY_STYLE}>
            <Text style={{ color: antGrey.grey9, fontSize: 16 }}>{description}</Text>
            <Tooltip title="Summary generated by AI">
                <img src={sparkleIcon} width={16} height={16} style={{ flexShrink: 0 }} />
            </Tooltip>
        </div>
    );
};

const UserReviewsSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.userReviews;
    if (!data) {
        return null;
    }
    const reviews = data.sampleReviews ?? [];
    if (reviews.length === 0) {
        return (
            <div style={ROW_STYLE}>
                <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                    {data.aiDescription}
                </Paragraph>
            </div>
        );
    }
    return (
        <>
            <AISummaryRow description={data.aiDescription} />
            {reviews.map((review, index) => (
                <div key={index} style={index === 0 ? ROW_STYLE : ROW_BORDER_STYLE}>
                    <Rate
                        disabled
                        value={Number(review.rating) || 0}
                        style={{ fontSize: 14, color: rwColors.statusYellow }}
                    />
                    {review.title && (
                        <Text style={{ fontSize: 14, color: antGrey.grey9, fontWeight: 600 }}>{review.title}</Text>
                    )}
                    {review.content && (
                        <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                            {review.content}
                        </Paragraph>
                    )}
                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                        {review.author}
                        {review.submittedAt ? ` · ${dayjs(review.submittedAt).format('MMM D, YYYY')}` : ''}
                    </Text>
                </div>
            ))}
        </>
    );
};

const BetaFeedbackSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.betaFeedback;
    if (!data) {
        return null;
    }
    const feedback = data.sampleFeedback ?? [];
    if (feedback.length === 0) {
        return (
            <div style={ROW_STYLE}>
                <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                    {data.aiDescription}
                </Paragraph>
            </div>
        );
    }
    return (
        <>
            <AISummaryRow description={data.aiDescription} />
            {feedback.map((item, index) => (
                <div key={index} style={index === 0 ? ROW_STYLE : ROW_BORDER_STYLE}>
                    {item.comment && (
                        <Paragraph style={{ color: antGrey.grey8, fontSize: 14, marginBottom: 0 }}>
                            {item.comment}
                        </Paragraph>
                    )}
                    {!!item.screenshotUrls?.length && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            <Image.PreviewGroup>
                                {item.screenshotUrls.map((url, i) => (
                                    <Image
                                        key={i}
                                        src={url}
                                        alt="Beta feedback screenshot"
                                        height={96}
                                        style={{
                                            borderRadius: 6,
                                            border: `1px solid ${antGrey.grey4}`,
                                            cursor: 'pointer',
                                        }}
                                    />
                                ))}
                            </Image.PreviewGroup>
                        </div>
                    )}
                    <Text style={{ color: antGrey.grey7, fontSize: 12 }}>
                        {[item.testerName, item.submittedAt ? dayjs(item.submittedAt).format('MMM D, YYYY') : '']
                            .filter(Boolean)
                            .join(' · ')}
                    </Text>
                </div>
            ))}
        </>
    );
};

const CONDITION_SYMBOL: Record<string, string> = {
    LessThan: '<',
    LessThanOrEqualTo: '≤',
    GreaterThan: '>',
    GreaterThanOrEqualTo: '≥',
};

const metricRow = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{label}</Text>
        <Text style={{ color: antGrey.grey9, fontSize: 14 }}>{value}</Text>
    </div>
);

const HealthMetricsSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.healthMetrics;
    if (!data) {
        return null;
    }
    const threshold = data.configuredThreshold;
    const metricName = data.metricName || data.eventName || data.metricId;
    const formatValue = (value: number) => Number(value.toFixed(2));
    return (
        <>
            <div style={ROW_STYLE}>
                {metricName && metricRow('Metric', metricName)}
                {data.metricDescription && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                        <Text style={{ color: antGrey.grey8, fontSize: 14, flexShrink: 0 }}>Description</Text>
                        <MarkdownParagraphWithEllipsis
                            content={data.metricDescription}
                            maxHeight="160px"
                            backgroundColor="transparent"
                            style={{ color: antGrey.grey9, fontSize: 14, textAlign: 'right' }}
                            markdownProps={{ components: DESCRIPTION_MARKDOWN_COMPONENTS }}
                            toggleAlign="right"
                        />
                    </div>
                )}
                {metricRow('Current value', formatValue(data.currentValue))}
                {threshold &&
                    metricRow(
                        'Threshold',
                        `${CONDITION_SYMBOL[threshold.condition] ?? ''} ${formatValue(threshold.value)}`.trim(),
                    )}
                {data.build && metricRow('Build', `#${data.build}`)}
            </div>
            {data.externalUrl && (
                <div style={ROW_BORDER_STYLE}>
                    <a
                        href={data.externalUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                    >
                        <ExportOutlined style={{ color: rwColors.primary, fontSize: 14 }} />
                        <Text style={{ color: rwColors.primary, fontSize: 14 }}>
                            View in {displayNameForIntegrationType(data.integrationType as IntegrationType)}
                        </Text>
                    </a>
                </div>
            )}
        </>
    );
};

const RunwaySource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.runway;
    if (!data) {
        return null;
    }
    const hasTitle = !!data.title;
    return (
        <>
            {hasTitle && (
                <div style={ROW_STYLE}>
                    <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{data.title}</Text>
                </div>
            )}
            {data.description && (
                <div style={hasTitle ? ROW_BORDER_STYLE : ROW_STYLE}>
                    <MarkdownParagraphWithEllipsis
                        content={data.description}
                        maxHeight="160px"
                        backgroundColor="transparent"
                        style={{ color: antGrey.grey8, fontSize: 14 }}
                        markdownProps={{ components: DESCRIPTION_MARKDOWN_COMPONENTS }}
                        toggleAlign="right"
                    />
                </div>
            )}
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const RegressionTestingSource: React.FC<{ source: IssueSource }> = ({ source }) => {
    const data = source.regressionTesting;
    if (!data) {
        return null;
    }
    return (
        <>
            <div style={ROW_STYLE}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
                    <Text style={{ fontSize: 16, color: antGrey.grey9 }}>{data.testRunName || 'Test run'}</Text>
                    {data.testRunUrl && (
                        <a
                            href={data.testRunUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }}
                        >
                            <ExportOutlined style={{ color: rwColors.primary, fontSize: 14 }} />
                            <Text style={{ color: rwColors.primary, fontSize: 14 }}>View run</Text>
                        </a>
                    )}
                </div>
                {data.failedTestCaseName && metricRow('Failed test case', data.failedTestCaseName)}
            </div>
            {data.build && <div style={ROW_BORDER_STYLE}>{metricRow('Build', `#${data.build}`)}</div>}
        </>
    );
};

const SourceFooter: React.FC<{
    createdAt: string;
    updatedAt: string;
    effectiveTimezone: string;
    use24HourTimeFormat: boolean;
}> = ({ createdAt, updatedAt, effectiveTimezone, use24HourTimeFormat }) => {
    const dateLabel = (date: string) => (
        <TimeWithConditionalTz
            triggeredAtLabel={formatDateWithTimezone(
                date,
                'longMonthDateYearTime',
                effectiveTimezone,
                use24HourTimeFormat,
            )}
            triggeredByMoment={dayjs(date)}
            use24HourTimeFormat={use24HourTimeFormat}
            effectiveTimezone={effectiveTimezone}
            style={{ color: 'inherit', fontSize: 'inherit' }}
        />
    );
    return (
        <div style={ROW_BORDER_STYLE}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Text style={{ color: antGrey.grey7, fontSize: 12 }}>Created {dateLabel(createdAt)}</Text>
                <Text style={{ color: antGrey.grey7, fontSize: 12 }}>Updated {dateLabel(updatedAt)}</Text>
            </div>
        </div>
    );
};

const UNMERGE_ACTIONS: {
    key: string;
    markNotAPriority: boolean;
    label: string;
    description: string;
    Icon: typeof MinusCircleOutlined;
}[] = [
    {
        key: 'unmerge',
        markNotAPriority: false,
        label: 'Unmerge issue...',
        description: 'This signal will be split out of this issue into a new issue.',
        Icon: MinusCircleOutlined,
    },
    {
        key: 'unmergeNotAPriority',
        markNotAPriority: true,
        label: "Unmerge and mark as 'Not a priority'...",
        description: "This signal will be split out of this issue into a new issue marked as 'Not a priority'.",
        Icon: CloseCircleOutlined,
    },
];

export const MetadataSourceSection: React.FC<{
    source: IssueSource;
    createdAt: string;
    updatedAt: string;
    effectiveTimezone: string;
    use24HourTimeFormat: boolean;
    appId: string;
    onUnmerge?: (markNotAPriority: boolean) => void;
    unmergeDisabled?: boolean;
    unmergeBusy?: boolean;
    unmergeLoading?: boolean;
}> = ({
    source,
    createdAt,
    updatedAt,
    effectiveTimezone,
    use24HourTimeFormat,
    appId,
    onUnmerge,
    unmergeDisabled,
    unmergeBusy,
    unmergeLoading,
}) => {
    let title = '';
    let body: React.ReactNode = null;
    const icon = <IssueSourceIcon source={source} appId={appId} size={18} />;
    if (source.type === IssueSourceType.PMTicket && source.pmTicket) {
        title = 'Ticket';
        body = <PMTicketSource source={source} />;
    } else if (source.type === IssueSourceType.UserReviews && source.userReviews) {
        title = 'User reviews';
        body = <UserReviewsSource source={source} />;
    } else if (source.type === IssueSourceType.BetaFeedback && source.betaFeedback) {
        title = 'Beta feedback';
        body = <BetaFeedbackSource source={source} />;
    } else if (source.type === IssueSourceType.StabMon && source.healthMetrics) {
        title = 'Stability monitoring';
        body = <HealthMetricsSource source={source} />;
    } else if (source.type === IssueSourceType.OBAA && source.healthMetrics) {
        title = 'Observability & analytics';
        body = <HealthMetricsSource source={source} />;
    } else if (source.type === IssueSourceType.Runway && source.runway) {
        title = 'Team';
        body = <RunwaySource source={source} />;
    } else if (source.type === IssueSourceType.RegressionTesting && source.regressionTesting) {
        title = 'Regression testing';
        body = <RegressionTestingSource source={source} />;
    }
    if (!body) {
        return null;
    }
    const ticketStatus = source.type === IssueSourceType.PMTicket ? source.pmTicket?.status : undefined;
    const ticketStatusColor = tagColorForStatusCategory(source.pmTicket?.statusCategory);
    const stepName = source.healthMetrics?.stepName;
    return (
        <Collapse
            className="triage-collapse"
            ghost
            bordered={false}
            items={[
                {
                    key: 'section',
                    showArrow: false,
                    style: { borderBottom: drawerSectionSeparator },
                    label: (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 16,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                {icon}
                                <Text style={{ color: antGrey.grey8, fontSize: 14 }}>
                                    {title}
                                    {stepName && (
                                        <>
                                            <Text style={{ color: antGrey.grey5, fontSize: 14, margin: '0 8px' }}>
                                                |
                                            </Text>
                                            <Text style={{ color: antGrey.grey8, fontSize: 14 }}>{stepName}</Text>
                                        </>
                                    )}
                                </Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {ticketStatus && (
                                    <Tag
                                        color={ticketStatusColor}
                                        style={{
                                            marginRight: 0,
                                            borderRadius: 4,
                                            textTransform: 'uppercase',
                                            fontSize: 10,
                                            fontWeight: 700,
                                            letterSpacing: '0.1em',
                                        }}
                                    >
                                        {ticketStatus}
                                    </Tag>
                                )}
                                {ticketStatus && onUnmerge && (
                                    <span
                                        style={{
                                            height: 20,
                                            width: 1,
                                            backgroundColor: antGrey.grey5,
                                            margin: '0 4px',
                                        }}
                                    />
                                )}
                                {onUnmerge && (
                                    <Dropdown
                                        trigger={['click']}
                                        placement="bottomRight"
                                        disabled={unmergeDisabled || unmergeBusy}
                                        menu={{
                                            items: UNMERGE_ACTIONS.map(({ key, label, Icon }) => ({
                                                key,
                                                icon: <Icon style={{ color: antGrey.grey7 }} />,
                                                label: <span style={{ color: antGrey.grey8 }}>{label}</span>,
                                            })),
                                            onClick: ({ key, domEvent }) => {
                                                domEvent.stopPropagation();
                                                const action = UNMERGE_ACTIONS.find((a) => a.key === key);
                                                if (!action) {
                                                    return;
                                                }
                                                Modal.confirm({
                                                    title: 'Unmerge signal',
                                                    content: action.description,
                                                    icon: null,
                                                    okText: 'Yes, unmerge',
                                                    okButtonProps: { danger: true },
                                                    cancelText: 'No',
                                                    onOk: () => onUnmerge(action.markNotAPriority),
                                                });
                                            },
                                        }}
                                    >
                                        <Tooltip
                                            title={unmergeDisabled ? DISALLOWED_ACTION_ERROR_MESSAGE : undefined}
                                            placement="left"
                                        >
                                            <Button
                                                size="small"
                                                type="text"
                                                disabled={unmergeDisabled || unmergeBusy}
                                                loading={unmergeLoading}
                                                icon={
                                                    unmergeLoading ? undefined : (
                                                        <EllipsisOutlined style={{ color: antGrey.grey7 }} />
                                                    )
                                                }
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ width: 20, height: 20, minWidth: 20, padding: 0 }}
                                            />
                                        </Tooltip>
                                    </Dropdown>
                                )}
                            </div>
                        </div>
                    ),
                    children: (
                        <>
                            {body}
                            <SourceFooter
                                createdAt={createdAt}
                                updatedAt={updatedAt}
                                effectiveTimezone={effectiveTimezone}
                                use24HourTimeFormat={use24HourTimeFormat}
                            />
                        </>
                    ),
                },
            ]}
        />
    );
};
